import { useEffect, useMemo, useState } from 'react';
import { Button } from 'antd';
import {
    DrawingManager, GoogleMap, Polygon, InfoWindow, useJsApiLoader,
} from '@react-google-maps/api';
import { IDangerZone, EDangerZoneStatus } from '../../types/danger-zone';
import { GOOGLE_MAPS_KEY } from '../../const/envs';
import { googleMapsStyles } from '../Map/googleMapStyles';
import { centerByCountry } from '../../utils/countries';

interface IDangerZoneMapProps {
    dangerZones: IDangerZone[];
    selectedCountry: string;
    onPolygonComplete: (polygon: google.maps.Polygon) => void;
    onMapLoad?: (map: google.maps.Map) => void;
    onZoneSelect?: (zone: IDangerZone) => void;
    onEditZone?: (zone: IDangerZone) => void;
    onDeleteZone?: (zone: IDangerZone) => void;
}

const mapOptions = {
    styles: googleMapsStyles,
};

const COUNTRY_BOUNDS = {
    EC: {
        north: 1.5,
        south: -5.0,
        east: -75.0,
        west: -81.5,
    },
    MX: {
        north: 25.0,
        south: 14.0,
        east: -86.0,
        west: -118.0,
    },
    CO: {
        north: 13.5,
        south: -4.5,
        east: -66.0,
        west: -79.0,
    },
};

export const DangerZoneMap: React.FC<IDangerZoneMapProps> = ({
    dangerZones,
    selectedCountry,
    onPolygonComplete,
    onMapLoad,
    onZoneSelect,
    onEditZone,
    onDeleteZone,
}) => {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_KEY,
        libraries: ['drawing', 'places'],
    });

    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [editingZone, setEditingZone] = useState<IDangerZone | null>(null);

    const [showInfoWindow, setShowInfoWindow] = useState({
        show: false,
        lat: 0,
        lng: 0,
        zoneName: '',
        notes: '',
    });

    const bounds = useMemo(() => COUNTRY_BOUNDS[selectedCountry as keyof typeof COUNTRY_BOUNDS], [selectedCountry]);
    const { zoom, center } = useMemo(() => ({
        zoom: 12,
        center: centerByCountry(selectedCountry),
    }), [selectedCountry]);

    useEffect(() => {
        if (map && bounds) {
            const countryBounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(bounds.south, bounds.west),
                new google.maps.LatLng(bounds.north, bounds.east),
            );

            // Actualizar restricciones
            map.setOptions({
                restriction: {
                    latLngBounds: countryBounds,
                    strictBounds: true,
                },
                minZoom: 8,
                maxZoom: 18,
                center,
                zoom,
            });

            // Limpiar cualquier polígono de dibujo existente
            const drawingManager = map.get('drawingManager');
            if (drawingManager) {
                drawingManager.setDrawingMode(null);
            }
        }
    }, [selectedCountry, map, bounds, center, zoom]);

    const getPolygonColor = (status: EDangerZoneStatus) => {
        switch (status) {
        case EDangerZoneStatus.YELLOW:
            return '#FFD700';
        case EDangerZoneStatus.ORANGE:
            return '#FFA500';
        case EDangerZoneStatus.RED:
            return '#FF0000';
        default:
            return '#FFD700';
        }
    };

    const dangerZonesToMap = useMemo(() => dangerZones.map((zone) => ({
        ...zone,
        paths: zone.geoLongLat.coordinates[0].map((coordinate: number[]) => ({
            lat: coordinate[1],
            lng: coordinate[0],
        })),
    })), [dangerZones]);

    const handleMapLoad = (mapInstance: google.maps.Map) => {
        setMap(mapInstance);

        const countryBounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(bounds.south, bounds.west),
            new google.maps.LatLng(bounds.north, bounds.east),
        );

        mapInstance.setOptions({
            restriction: {
                latLngBounds: countryBounds,
                strictBounds: true,
            },
            minZoom: 8,
            maxZoom: 18,
        });

        if (onMapLoad) {
            onMapLoad(mapInstance);
        }
    };

    const handleMapClick = (event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();

            if (lat < bounds.south || lat > bounds.north || lng < bounds.west || lng > bounds.east) {
                return;
            }
        }
    };

    if (!isLoaded) {
        return <div>Loading map...</div>;
    }

    return (
        <div style={{ height: '780px', width: '100%' }}>
            <GoogleMap
                mapContainerStyle={{ height: '100%', width: '100%' }}
                options={mapOptions}
                onLoad={handleMapLoad}
                onClick={handleMapClick}
            >
                <DrawingManager
                    onPolygonComplete={onPolygonComplete}
                    options={{
                        drawingControl: true,
                        drawingControlOptions: {
                            position: google.maps.ControlPosition.TOP_CENTER,
                            drawingModes: [google.maps.drawing.OverlayType.POLYGON],
                        },
                        polygonOptions: {
                            fillColor: '#FFD700',
                            fillOpacity: 0.3,
                            strokeWeight: 2,
                            strokeColor: '#FFD700',
                            clickable: true,
                            editable: true,
                            zIndex: 1,
                        },
                    }}
                />

                {dangerZonesToMap.map((zone) => (
                    <Polygon
                        key={zone._id}
                        paths={zone.paths}
                        options={{
                            fillColor: getPolygonColor(zone.status),
                            fillOpacity: 0.3,
                            strokeWeight: 2,
                            strokeColor: getPolygonColor(zone.status),
                            clickable: true,
                        }}
                        onClick={() => {
                            setEditingZone(zone);
                            setShowInfoWindow({
                                show: true,
                                lat: zone.paths[0].lat,
                                lng: zone.paths[0].lng,
                                zoneName: `Danger Zone - ${zone.status}`,
                                notes: zone.notes || 'No notes',
                            });
                            if (onZoneSelect) {
                                onZoneSelect(zone);
                            }
                        }}
                    />
                ))}

                {showInfoWindow.show && (
                    <InfoWindow
                        position={{
                            lat: showInfoWindow.lat,
                            lng: showInfoWindow.lng,
                        }}
                        onCloseClick={() => setShowInfoWindow({ ...showInfoWindow, show: false })}
                    >
                        <div style={{ color: 'black', minWidth: '200px' }}>
                            <h3 style={{ color: 'black', marginBottom: '8px' }}>{showInfoWindow.zoneName}</h3>
                            <p style={{ color: 'black', marginBottom: '12px' }}>{showInfoWindow.notes}</p>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    type="primary"
                                    onClick={() => {
                                        if (onEditZone && editingZone) {
                                            onEditZone(editingZone);
                                            setShowInfoWindow({ ...showInfoWindow, show: false });
                                        }
                                    }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size="small"
                                    danger
                                    onClick={() => {
                                        if (onDeleteZone && editingZone) {
                                            onDeleteZone(editingZone);
                                            setShowInfoWindow({ ...showInfoWindow, show: false });
                                        }
                                    }}
                                >
                                    Delete
                                </Button>
                            </div>
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
        </div>
    );
};
